import type {
  ConfirmMemberRechargeReconciliationRequest,
  ManualReviewReadItem,
  MemberRechargeReconciliationPreview,
  MemberRechargeReconciliationPreviewRequest,
  MemberRechargeReconciliationResult,
  MemberRechargeRecord,
  MemberRechargeRequest,
  MemberRecord,
  RechargePlanRecord,
  WalletAllocationInput,
} from "@billiard/contracts";
import {
  isManualReviewListReadModel,
  isMemberRechargeReconciliationPreview,
  isMemberRechargeReconciliationPreviewRequest,
  isMemberRechargeReconciliationResult,
  isMemberRechargeRecordList,
  isMemberRecord,
  isRechargePlanRecordList,
  MANUAL_REVIEW_READ_CAPACITY,
} from "@billiard/contracts";

export const MEMBER_RECHARGE_RECOVERY_STORAGE_KEY = "billiard.ops-web.member-recharge-recovery.v1";

export interface MemberRechargeRecoveryContext {
  tenantId: string;
  storeId: string;
  operatorId: string;
}

export interface MemberRechargeCreateCommand {
  kind: "MEMBER_RECHARGE";
  memberId: string;
  request: MemberRechargeRequest;
  expectedPlan: MemberRechargePlanFinancialSnapshot | null;
}

export interface MemberRechargePlanFinancialSnapshot {
  planId: string;
  planVersion: number;
  paidAmountFen: number;
  principalFen: number;
  bonusFen: number;
  allocations: WalletAllocationInput[];
}

export interface MemberRechargeReconciliationPreviewCommand {
  kind: "MEMBER_RECHARGE_RECONCILIATION_PREVIEW";
  workItemId: string;
  baselineReview: MemberRechargeManualReviewItem;
  request: MemberRechargeReconciliationPreviewRequest;
}

export interface MemberRechargeReconciliationConfirmationCommand {
  kind: "MEMBER_RECHARGE_RECONCILIATION_CONFIRM";
  workItemId: string;
  baselineReview: MemberRechargeManualReviewItem;
  previewRequest: MemberRechargeReconciliationPreviewRequest;
  preview: MemberRechargeReconciliationPreview;
  request: ConfirmMemberRechargeReconciliationRequest;
}

export type MemberRechargeManualReviewItem = Extract<
  ManualReviewReadItem,
  { category: "MEMBER_RECHARGE_RECONCILIATION" }
>;

export type MemberRechargeCommand =
  | MemberRechargeCreateCommand
  | MemberRechargeReconciliationPreviewCommand
  | MemberRechargeReconciliationConfirmationCommand;

export type MemberRechargeRecoveryState = "CLAIMED" | "OUTCOME_UNKNOWN";

export interface MemberRechargeRecovery extends MemberRechargeRecoveryContext {
  command: MemberRechargeCommand;
  state: MemberRechargeRecoveryState;
}

export type MemberRechargeRecoveryView =
  | { status: "NONE"; canRetry: false }
  | { status: "OWNED"; recovery: MemberRechargeRecovery; canRetry: boolean }
  | { status: "CONTEXT_MISMATCH" | "COMMAND_MISMATCH"; canRetry: false }
  | {
      status: "RECOVERY_STORAGE_UNAVAILABLE";
      canRetry: false;
      pendingOutcomeMayExist: boolean;
    };

export interface MemberRechargeCreateResult {
  recharge: MemberRechargeRecord;
  member: MemberRecord;
}

export type MemberRechargeResult =
  | MemberRechargeCreateResult
  | MemberRechargeReconciliationPreview
  | MemberRechargeReconciliationResult;

interface FailureShape {
  code?: unknown;
  statusCode?: unknown;
}

let pendingRecovery: MemberRechargeRecovery | null = null;
let dispatchingRecovery: MemberRechargeRecovery | null = null;
let hydrated = false;
let recoveryUnavailable = false;
let pendingOutcomeMayExist = false;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: Record<string, unknown>, keys: readonly string[]): boolean {
  return (
    Object.keys(value).length === keys.length && keys.every((key) => Object.hasOwn(value, key))
  );
}

function isCanonicalId(value: unknown, maximum = 128): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= maximum &&
    /^[A-Za-z0-9][A-Za-z0-9._:-]*$/u.test(value)
  );
}

function isBoundedString(value: unknown, minimum: number, maximum: number): value is string {
  return typeof value === "string" && [...value].length >= minimum && [...value].length <= maximum;
}

function confirmationRequestIsValid(
  value: unknown,
): value is ConfirmMemberRechargeReconciliationRequest {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["previewId", "confirmationToken", "idempotencyKey"]) &&
    typeof value.previewId === "string" &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(
      value.previewId,
    ) &&
    typeof value.confirmationToken === "string" &&
    /^[a-f0-9]{64}$/u.test(value.confirmationToken) &&
    isBoundedString(value.idempotencyKey, 8, 128)
  );
}

function contextIsValid(value: unknown): value is MemberRechargeRecoveryContext {
  return (
    isRecord(value) &&
    isBoundedString(value.tenantId, 1, 256) &&
    isBoundedString(value.storeId, 1, 256) &&
    isBoundedString(value.operatorId, 1, 256)
  );
}

function allocationIsValid(value: unknown): value is WalletAllocationInput {
  return (
    isRecord(value) &&
    hasExactKeys(value, ["walletDefinitionId", "amount"]) &&
    isCanonicalId(value.walletDefinitionId, 64) &&
    Number.isSafeInteger(value.amount) &&
    Number(value.amount) >= 1 &&
    Number(value.amount) <= 100_000_000
  );
}

function allocationsAreValid(value: unknown): value is WalletAllocationInput[] | null {
  if (value === null) return true;
  if (!Array.isArray(value) || value.length < 1 || value.length > 20) return false;
  const walletIds = new Set<string>();
  for (const allocation of value) {
    if (!allocationIsValid(allocation) || walletIds.has(allocation.walletDefinitionId))
      return false;
    walletIds.add(allocation.walletDefinitionId);
  }
  return true;
}

function rechargeRequestIsValid(value: unknown): value is MemberRechargeRequest {
  if (!isRecord(value)) return false;
  const hasAllocations = Object.hasOwn(value, "customAllocations");
  if (
    !hasExactKeys(
      value,
      hasAllocations
        ? [
            "planId",
            "expectedPlanVersion",
            "customPrincipalFen",
            "customAllocations",
            "method",
            "note",
            "idempotencyKey",
          ]
        : [
            "planId",
            "expectedPlanVersion",
            "customPrincipalFen",
            "method",
            "note",
            "idempotencyKey",
          ],
    ) ||
    !(value.planId === null || isCanonicalId(value.planId, 64)) ||
    !(
      value.expectedPlanVersion === null ||
      (Number.isSafeInteger(value.expectedPlanVersion) && Number(value.expectedPlanVersion) >= 1)
    ) ||
    !(
      value.customPrincipalFen === null ||
      (Number.isSafeInteger(value.customPrincipalFen) &&
        Number(value.customPrincipalFen) >= 1 &&
        Number(value.customPrincipalFen) <= 100_000_000)
    ) ||
    !["CASH", "STATIC_WECHAT", "STATIC_ALIPAY"].includes(String(value.method)) ||
    typeof value.note !== "string" ||
    [...value.note].length > 200 ||
    !isBoundedString(value.idempotencyKey, 8, 128)
  ) {
    return false;
  }
  if (hasAllocations && !allocationsAreValid(value.customAllocations)) return false;
  if (value.planId === null) {
    return (
      value.customPrincipalFen !== null &&
      value.expectedPlanVersion === null &&
      hasAllocations &&
      Array.isArray(value.customAllocations) &&
      value.customAllocations.length === 1 &&
      allocationIsValid(value.customAllocations[0]) &&
      value.customAllocations[0].amount === value.customPrincipalFen
    );
  }
  return (
    value.expectedPlanVersion !== null &&
    value.customPrincipalFen === null &&
    (!hasAllocations || value.customAllocations === null)
  );
}

function memberRechargeManualReviewIsValid(
  value: unknown,
): value is MemberRechargeManualReviewItem {
  if (!isRecord(value) || typeof value.createdAt !== "string") return false;
  if (
    !isManualReviewListReadModel({
      items: [value],
      pendingCount: 1,
      capacity: MANUAL_REVIEW_READ_CAPACITY,
      generatedAt: value.createdAt,
    })
  ) {
    return false;
  }
  const item = value as ManualReviewReadItem;
  if (item.category !== "MEMBER_RECHARGE_RECONCILIATION") return false;
  return (
    // itemKey is the work-center list identity; workItemId is the original
    // recharge command target. The Gateway deliberately publishes them as
    // different stable identifiers, and the list contract validates both.
    Number.isSafeInteger(item.amountFen) &&
    Number(item.amountFen) >= 1 &&
    Number(item.amountFen) <= 100_000_000 &&
    !item.offlineOrigin &&
    (item.rechargeStatus === "PENDING_RECONCILIATION"
      ? item.latestReconciliationNote === null
      : item.latestReconciliationNote !== null)
  );
}

function planFinancialSnapshotIsValid(
  value: unknown,
): value is MemberRechargePlanFinancialSnapshot {
  return (
    isRecord(value) &&
    hasExactKeys(value, [
      "planId",
      "planVersion",
      "paidAmountFen",
      "principalFen",
      "bonusFen",
      "allocations",
    ]) &&
    isCanonicalId(value.planId, 64) &&
    Number.isSafeInteger(value.planVersion) &&
    Number(value.planVersion) >= 1 &&
    Number.isSafeInteger(value.paidAmountFen) &&
    Number(value.paidAmountFen) >= 1 &&
    Number(value.paidAmountFen) <= 100_000_000 &&
    Number.isSafeInteger(value.principalFen) &&
    Number(value.principalFen) >= 0 &&
    Number(value.principalFen) <= 100_000_000 &&
    Number.isSafeInteger(value.bonusFen) &&
    Number(value.bonusFen) >= 0 &&
    Number(value.bonusFen) <= 100_000_000 &&
    allocationsAreValid(value.allocations) &&
    value.allocations !== null
  );
}

function rechargeRecordIsValid(value: unknown): value is MemberRechargeRecord {
  return isMemberRechargeRecordList([value], false);
}

function paymentEvidenceMatches(
  paymentEvidenceReference: string | null,
  paymentEvidenceEnding: string | null,
): boolean {
  return paymentEvidenceReference === null
    ? paymentEvidenceEnding === null
    : paymentEvidenceEnding !== null &&
        paymentEvidenceReference.trim().toUpperCase().slice(-8) === paymentEvidenceEnding;
}

function walletCreditsEqual(
  left: readonly MemberRechargeReconciliationPreview["walletCredits"][number][],
  right: readonly MemberRechargeReconciliationPreview["walletCredits"][number][],
): boolean {
  return (
    left.length === right.length &&
    left.every((credit, index) => {
      const expected = right[index];
      return (
        expected !== undefined &&
        credit.walletName === expected.walletName &&
        credit.unit === expected.unit &&
        credit.customUnitLabel === expected.customUnitLabel &&
        credit.precision === expected.precision &&
        credit.businessType === expected.businessType &&
        credit.amount === expected.amount
      );
    })
  );
}

export function memberRechargeReconciliationPreviewMatchesCommand(
  value: unknown,
  command: MemberRechargeReconciliationPreviewCommand,
): value is MemberRechargeReconciliationPreview {
  if (
    !memberRechargeManualReviewIsValid(command.baselineReview) ||
    command.workItemId !== command.baselineReview.workItemId ||
    !isMemberRechargeReconciliationPreviewRequest(command.request) ||
    !isMemberRechargeReconciliationPreview(value)
  ) {
    return false;
  }
  const baselineCreatedAt = Date.parse(command.baselineReview.createdAt);
  const previewCreatedAt = Date.parse(value.createdAt);
  return (
    value.workItemId === command.workItemId &&
    value.outcome === command.request.outcome &&
    value.memberName === command.baselineReview.memberName &&
    value.method === command.baselineReview.method &&
    value.requestedPaidAmountFen === command.baselineReview.amountFen &&
    value.actualReceivedFen === command.request.actualReceivedFen &&
    paymentEvidenceMatches(command.request.paymentEvidenceReference, value.paymentEvidenceEnding) &&
    value.note === command.request.note &&
    Number.isFinite(baselineCreatedAt) &&
    Number.isFinite(previewCreatedAt) &&
    previewCreatedAt >= baselineCreatedAt
  );
}

export function memberRechargeReconciliationResultMatchesCommand(
  value: unknown,
  command: MemberRechargeReconciliationConfirmationCommand,
): value is MemberRechargeReconciliationResult {
  if (
    !memberRechargeReconciliationPreviewMatchesCommand(command.preview, {
      kind: "MEMBER_RECHARGE_RECONCILIATION_PREVIEW",
      workItemId: command.workItemId,
      baselineReview: command.baselineReview,
      request: command.previewRequest,
    }) ||
    !confirmationRequestIsValid(command.request) ||
    command.request.previewId !== command.preview.previewId ||
    command.request.confirmationToken !== command.preview.confirmationToken ||
    !isMemberRechargeReconciliationResult(value)
  ) {
    return false;
  }
  const reconciledAt = Date.parse(value.reconciledAt);
  const previewCreatedAt = Date.parse(command.preview.createdAt);
  const previewExpiresAt = Date.parse(command.preview.expiresAt);
  return (
    value.workItemId === command.workItemId &&
    value.previewId === command.preview.previewId &&
    value.outcome === command.preview.outcome &&
    value.memberName === command.preview.memberName &&
    value.method === command.preview.method &&
    value.requestedPaidAmountFen === command.preview.requestedPaidAmountFen &&
    value.actualReceivedFen === command.preview.actualReceivedFen &&
    value.cnyWalletCreditTotalFen === command.preview.cnyWalletCreditTotalFen &&
    walletCreditsEqual(value.walletCredits, command.preview.walletCredits) &&
    value.paymentEvidenceEnding === command.preview.paymentEvidenceEnding &&
    value.note === command.preview.note &&
    value.balanceEffect === command.preview.balanceEffectOnConfirmation &&
    Number.isFinite(reconciledAt) &&
    reconciledAt >= previewCreatedAt &&
    reconciledAt <= previewExpiresAt
  );
}

function commandIsValid(value: unknown): value is MemberRechargeCommand {
  if (!isRecord(value)) return false;
  if (value.kind === "MEMBER_RECHARGE") {
    return (
      hasExactKeys(value, ["kind", "memberId", "request", "expectedPlan"]) &&
      isCanonicalId(value.memberId) &&
      rechargeRequestIsValid(value.request) &&
      (value.request.planId === null
        ? value.expectedPlan === null
        : planFinancialSnapshotIsValid(value.expectedPlan) &&
          value.expectedPlan.planId === value.request.planId &&
          value.expectedPlan.planVersion === value.request.expectedPlanVersion)
    );
  }
  if (value.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW") {
    return (
      hasExactKeys(value, ["kind", "workItemId", "baselineReview", "request"]) &&
      memberRechargeManualReviewIsValid(value.baselineReview) &&
      value.baselineReview.workItemId === value.workItemId &&
      isMemberRechargeReconciliationPreviewRequest(value.request)
    );
  }
  return (
    value.kind === "MEMBER_RECHARGE_RECONCILIATION_CONFIRM" &&
    hasExactKeys(value, [
      "kind",
      "workItemId",
      "baselineReview",
      "previewRequest",
      "preview",
      "request",
    ]) &&
    memberRechargeManualReviewIsValid(value.baselineReview) &&
    value.baselineReview.workItemId === value.workItemId &&
    isMemberRechargeReconciliationPreviewRequest(value.previewRequest) &&
    memberRechargeReconciliationPreviewMatchesCommand(value.preview, {
      kind: "MEMBER_RECHARGE_RECONCILIATION_PREVIEW",
      workItemId: value.workItemId,
      baselineReview: value.baselineReview,
      request: value.previewRequest,
    }) &&
    confirmationRequestIsValid(value.request) &&
    value.request.previewId === value.preview.previewId &&
    value.request.confirmationToken === value.preview.confirmationToken
  );
}

function freezeDeep(value: unknown): void {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return;
  for (const child of Object.values(value)) freezeDeep(child);
  Object.freeze(value);
}

function immutableRecovery(
  context: MemberRechargeRecoveryContext,
  command: MemberRechargeCommand,
  state: MemberRechargeRecoveryState,
): MemberRechargeRecovery {
  const snapshot = structuredClone({ ...context, command, state }) as MemberRechargeRecovery;
  freezeDeep(snapshot);
  return snapshot;
}

function storedRecovery(value: unknown): MemberRechargeRecovery | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "recovery"]) ||
    value.version !== 1 ||
    !isRecord(value.recovery) ||
    !hasExactKeys(value.recovery, ["tenantId", "storeId", "operatorId", "command", "state"])
  ) {
    return null;
  }
  const context = {
    tenantId: value.recovery.tenantId,
    storeId: value.recovery.storeId,
    operatorId: value.recovery.operatorId,
  };
  if (
    !contextIsValid(context) ||
    !commandIsValid(value.recovery.command) ||
    (value.recovery.state !== "CLAIMED" && value.recovery.state !== "OUTCOME_UNKNOWN")
  ) {
    return null;
  }
  return immutableRecovery(
    context,
    value.recovery.command,
    value.recovery.state as MemberRechargeRecoveryState,
  );
}

function storageAccess(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

function recoveryEquals(left: MemberRechargeRecovery, right: MemberRechargeRecovery): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function commandEquals(left: MemberRechargeCommand, right: MemberRechargeCommand): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function readStored(storage: Storage): MemberRechargeRecovery | null {
  const encoded = storage.getItem(MEMBER_RECHARGE_RECOVERY_STORAGE_KEY);
  return encoded === null ? null : storedRecovery(JSON.parse(encoded) as unknown);
}

function saveStored(recovery: MemberRechargeRecovery): boolean {
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) return false;
  try {
    storage.setItem(MEMBER_RECHARGE_RECOVERY_STORAGE_KEY, JSON.stringify({ version: 1, recovery }));
    const stored = readStored(storage);
    return stored !== null && recoveryEquals(stored, recovery);
  } catch {
    return false;
  }
}

function hydrate(): void {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === "undefined") return;
  const storage = storageAccess();
  if (!storage) {
    recoveryUnavailable = true;
    return;
  }
  try {
    const encoded = storage.getItem(MEMBER_RECHARGE_RECOVERY_STORAGE_KEY);
    if (encoded === null) return;
    pendingOutcomeMayExist = true;
    const stored = storedRecovery(JSON.parse(encoded) as unknown);
    if (!stored) {
      recoveryUnavailable = true;
      return;
    }
    const recovered =
      stored.state === "CLAIMED"
        ? immutableRecovery(stored, stored.command, "OUTCOME_UNKNOWN")
        : stored;
    if (stored.state === "CLAIMED" && !saveStored(recovered)) {
      recoveryUnavailable = true;
      return;
    }
    pendingRecovery = recovered;
  } catch {
    recoveryUnavailable = true;
  }
}

function contextMatches(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
): boolean {
  return (
    recovery.tenantId === context.tenantId &&
    recovery.storeId === context.storeId &&
    recovery.operatorId === context.operatorId
  );
}

function unavailableView(): MemberRechargeRecoveryView {
  return {
    status: "RECOVERY_STORAGE_UNAVAILABLE",
    canRetry: false,
    pendingOutcomeMayExist,
  };
}

export function memberRechargeRecoveryContext(operator: {
  tenantId: string;
  storeId: string;
  id: string;
}): MemberRechargeRecoveryContext {
  return { tenantId: operator.tenantId, storeId: operator.storeId, operatorId: operator.id };
}

export function memberRechargePlanFinancialSnapshot(
  plan: RechargePlanRecord,
): MemberRechargePlanFinancialSnapshot | null {
  if (!isRechargePlanRecordList([plan], true)) return null;
  return {
    planId: plan.id,
    planVersion: plan.version,
    paidAmountFen: plan.expectedRecharge.paidAmountFen,
    principalFen: plan.expectedRecharge.principalFen,
    bonusFen: plan.expectedRecharge.bonusFen,
    allocations: plan.expectedRecharge.allocations.map((allocation) => ({ ...allocation })),
  };
}

export function memberRechargeCommand(
  memberId: string,
  request: MemberRechargeRequest,
  expectedPlan: MemberRechargePlanFinancialSnapshot | null,
): MemberRechargeCreateCommand {
  return { kind: "MEMBER_RECHARGE", memberId, request, expectedPlan };
}

export function memberRechargeReconciliationPreviewCommand(
  baselineReview: MemberRechargeManualReviewItem,
  request: MemberRechargeReconciliationPreviewRequest,
): MemberRechargeReconciliationPreviewCommand {
  return {
    kind: "MEMBER_RECHARGE_RECONCILIATION_PREVIEW",
    workItemId: baselineReview.workItemId,
    baselineReview,
    request,
  };
}

export function readMemberRechargeRecovery(
  context: MemberRechargeRecoveryContext,
): MemberRechargeRecoveryView {
  hydrate();
  if (recoveryUnavailable || !contextIsValid(context)) return unavailableView();
  if (!pendingRecovery) return { status: "NONE", canRetry: false };
  if (!contextMatches(pendingRecovery, context)) {
    return { status: "CONTEXT_MISMATCH", canRetry: false };
  }
  return {
    status: "OWNED",
    recovery: pendingRecovery,
    canRetry: pendingRecovery.state === "OUTCOME_UNKNOWN",
  };
}

export function memberRechargeRecoveryBlocksWrites(): boolean {
  hydrate();
  return recoveryUnavailable || pendingRecovery !== null;
}

export function claimMemberRechargeRecovery(
  context: MemberRechargeRecoveryContext,
  command: MemberRechargeCommand,
): MemberRechargeRecoveryView {
  const current = readMemberRechargeRecovery(context);
  if (current.status === "OWNED") {
    return commandEquals(current.recovery.command, command)
      ? current
      : { status: "COMMAND_MISMATCH", canRetry: false };
  }
  if (current.status !== "NONE") return current;
  if (!contextIsValid(context) || !commandIsValid(command)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  const recovery = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(recovery)) {
    recoveryUnavailable = true;
    return unavailableView();
  }
  pendingRecovery = recovery;
  pendingOutcomeMayExist = true;
  return { status: "OWNED", recovery, canRetry: false };
}

function verify(recovery: MemberRechargeRecovery, context: MemberRechargeRecoveryContext): boolean {
  hydrate();
  if (recoveryUnavailable || pendingRecovery !== recovery || !contextMatches(recovery, context)) {
    return false;
  }
  if (typeof window === "undefined") return true;
  const storage = storageAccess();
  if (!storage) {
    recoveryUnavailable = true;
    return false;
  }
  try {
    const stored = readStored(storage);
    if (!stored || !recoveryEquals(stored, recovery)) {
      recoveryUnavailable = true;
      return false;
    }
    return true;
  } catch {
    recoveryUnavailable = true;
    return false;
  }
}

export function beginMemberRechargeDispatch(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
): boolean {
  if (dispatchingRecovery || !verify(recovery, context)) return false;
  dispatchingRecovery = recovery;
  return true;
}

export function finishMemberRechargeDispatch(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
): boolean {
  if (dispatchingRecovery !== recovery || !contextMatches(recovery, context)) return false;
  dispatchingRecovery = null;
  return true;
}

export function advanceMemberRechargeRecoveryToConfirmation(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
  preview: unknown,
  request: ConfirmMemberRechargeReconciliationRequest,
): MemberRechargeRecovery | null {
  if (
    dispatchingRecovery !== recovery ||
    recovery.command.kind !== "MEMBER_RECHARGE_RECONCILIATION_PREVIEW" ||
    !verify(recovery, context) ||
    !memberRechargeReconciliationPreviewMatchesCommand(preview, recovery.command) ||
    !confirmationRequestIsValid(request) ||
    request.previewId !== preview.previewId ||
    request.confirmationToken !== preview.confirmationToken
  ) {
    return null;
  }
  const command: MemberRechargeReconciliationConfirmationCommand = {
    kind: "MEMBER_RECHARGE_RECONCILIATION_CONFIRM",
    workItemId: recovery.command.workItemId,
    baselineReview: recovery.command.baselineReview,
    previewRequest: recovery.command.request,
    preview,
    request,
  };
  if (!commandIsValid(command)) return null;
  const advanced = immutableRecovery(context, command, "CLAIMED");
  if (!saveStored(advanced)) {
    recoveryUnavailable = true;
    return null;
  }
  pendingRecovery = advanced;
  dispatchingRecovery = null;
  pendingOutcomeMayExist = true;
  return advanced;
}

export function markMemberRechargeOutcomeUnknown(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
): boolean {
  if (!verify(recovery, context)) return false;
  const unknown =
    recovery.state === "OUTCOME_UNKNOWN"
      ? recovery
      : immutableRecovery(context, recovery.command, "OUTCOME_UNKNOWN");
  if (!saveStored(unknown)) {
    recoveryUnavailable = true;
    return false;
  }
  pendingRecovery = unknown;
  pendingOutcomeMayExist = true;
  return true;
}

function clearRecovery(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
): boolean {
  if (!verify(recovery, context)) return false;
  if (typeof window !== "undefined") {
    const storage = storageAccess();
    if (!storage) {
      recoveryUnavailable = true;
      return false;
    }
    try {
      storage.removeItem(MEMBER_RECHARGE_RECOVERY_STORAGE_KEY);
      if (storage.getItem(MEMBER_RECHARGE_RECOVERY_STORAGE_KEY) !== null) {
        recoveryUnavailable = true;
        return false;
      }
    } catch {
      recoveryUnavailable = true;
      return false;
    }
  }
  if (dispatchingRecovery === recovery) dispatchingRecovery = null;
  pendingRecovery = null;
  pendingOutcomeMayExist = false;
  return true;
}

export function discardUnconfirmedMemberRechargeReconciliation(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
): boolean {
  const isUnconfirmedReconciliation =
    recovery.command.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW" ||
    recovery.command.kind === "MEMBER_RECHARGE_RECONCILIATION_CONFIRM";
  return (
    isUnconfirmedReconciliation &&
    recovery.state === "CLAIMED" &&
    dispatchingRecovery === null &&
    clearRecovery(recovery, context)
  );
}

function allocationsEqual(
  left: readonly WalletAllocationInput[],
  right: readonly WalletAllocationInput[],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function memberRechargeResultMatchesCommand(
  value: unknown,
  command: MemberRechargeCommand,
): value is MemberRechargeResult {
  if (!commandIsValid(command)) return false;
  if (command.kind === "MEMBER_RECHARGE_RECONCILIATION_PREVIEW") {
    return memberRechargeReconciliationPreviewMatchesCommand(value, command);
  }
  if (command.kind === "MEMBER_RECHARGE_RECONCILIATION_CONFIRM") {
    return memberRechargeReconciliationResultMatchesCommand(value, command);
  }
  const expectedMemberId = command.memberId;
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["recharge", "member"]) ||
    !isMemberRecord(value.member, expectedMemberId) ||
    !rechargeRecordIsValid(value.recharge)
  ) {
    return false;
  }
  const recharge = value.recharge;
  if (recharge.allocations === undefined || recharge.paidAmountFen === undefined) return false;
  if (recharge.memberId !== expectedMemberId || recharge.memberId !== value.member.id) return false;
  const request = command.request;
  if (
    recharge.method !== request.method ||
    recharge.planId !== request.planId ||
    recharge.note !== request.note ||
    recharge.reconciledAt !== null ||
    recharge.evidence !== "MANUAL_FALLBACK" ||
    (request.method === "CASH"
      ? recharge.status !== "CONFIRMED_LOCAL"
      : recharge.status !== "PENDING_RECONCILIATION")
  ) {
    return false;
  }
  if (request.planId !== null) {
    const expectedPlan = command.expectedPlan;
    return (
      expectedPlan !== null &&
      recharge.paidAmountFen === expectedPlan.paidAmountFen &&
      recharge.principalFen === expectedPlan.principalFen &&
      recharge.bonusFen === expectedPlan.bonusFen &&
      allocationsEqual(recharge.allocations, expectedPlan.allocations)
    );
  }
  if (
    request.customPrincipalFen === null ||
    recharge.paidAmountFen !== request.customPrincipalFen ||
    recharge.principalFen !== request.customPrincipalFen ||
    recharge.bonusFen !== 0
  ) {
    return false;
  }
  return request.customAllocations !== undefined && request.customAllocations !== null
    ? allocationsEqual(recharge.allocations, request.customAllocations)
    : false;
}

export function completeMemberRechargeRecovery(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
  result: unknown,
): result is MemberRechargeResult {
  return (
    dispatchingRecovery === recovery &&
    verify(recovery, context) &&
    recovery.command.kind !== "MEMBER_RECHARGE_RECONCILIATION_PREVIEW" &&
    memberRechargeResultMatchesCommand(result, recovery.command) &&
    clearRecovery(recovery, context)
  );
}

export function memberRechargeOutcomeIsUnknown(error: unknown): boolean {
  if (!isRecord(error)) return true;
  const { code, statusCode } = error as FailureShape;
  if (
    code === "GATEWAY_UNREACHABLE" ||
    code === "GATEWAY_RESPONSE_INVALID" ||
    code === "MEMBER_RECHARGE_RECONCILIATION_RESULT_UNKNOWN" ||
    code === "OPERATOR_IDENTITY_CHANGED"
  ) {
    return true;
  }
  const isExplicitNamedPreWriteRejection =
    typeof code === "string" &&
    code.length > 0 &&
    code !== "REQUEST_FAILED" &&
    typeof statusCode === "number" &&
    Number.isInteger(statusCode) &&
    statusCode >= 400 &&
    statusCode < 500 &&
    statusCode !== 408;
  return !isExplicitNamedPreWriteRejection;
}

export function clearMemberRechargeRecoveryAfterDefiniteRejection(
  recovery: MemberRechargeRecovery,
  context: MemberRechargeRecoveryContext,
  error: unknown,
): boolean {
  return !memberRechargeOutcomeIsUnknown(error) && clearRecovery(recovery, context);
}

export function resetMemberRechargeRecoveryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
  try {
    storageAccess()?.removeItem(MEMBER_RECHARGE_RECOVERY_STORAGE_KEY);
  } catch {
    // The next test starts from a fresh in-memory fail-closed state.
  }
}

export function resetMemberRechargeRecoveryMemoryForTests(): void {
  pendingRecovery = null;
  dispatchingRecovery = null;
  hydrated = false;
  recoveryUnavailable = false;
  pendingOutcomeMayExist = false;
}
